import React, { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';

// Type definitions
interface ProsConsItem {
  id: number;
  select_type: 'Pros' | 'Cons';
  heading: string;
}

interface ProsConsData {
  pros: ProsConsItem[];
  cons: ProsConsItem[];
}

interface ApiResponse {
  success?: boolean;
  data: ProsConsItem[] | null;
}

const CareerProsCons: React.FC = () => {
  const [prosConsData, setProsConsData] = useState<ProsConsData>({ pros: [], cons: [] });
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProsCons = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await axios.post<ApiResponse>(
          `${process.env.REACT_APP_SFE_API}career/fetchProsCons`,
          { career_id: id },
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );

        if (response.data && response.data.data) {
          const pros = response.data.data.filter(
            (item) => item.select_type === 'Pros'
          );
          const cons = response.data.data.filter(
            (item) => item.select_type === 'Cons'
          );
          setProsConsData({ pros, cons });
        } else {
          // If data is null, you can optionally set an error message
          // setError('No Pros and Cons data available.');
        }
      } catch (error) {
        console.error('Error fetching Pros and Cons:', error);
        const errorMessage = error instanceof AxiosError 
          ? error.message 
          : 'Failed to load Pros and Cons data.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchProsCons();
  }, [id]);

  if (loading) {
    return <p>Loading Pros &amp; Cons...</p>;
  }

  // Check if there are any pros or cons to display
  if (prosConsData.pros.length === 0 && prosConsData.cons.length === 0) {
    return null;
  }

  return (
    <section className="container-fluid py-5 position-relative z-0" id="prosCons">
      <div className="max-1140 mx-auto">
        <h1 className="font-32 font-900 mb-3 text-dark-blue">Pros &amp; Cons</h1>
        <div className="row g-4">
          {prosConsData.pros.length > 0 && (
            <div className="col-sm-6">
              <div className="alert alert-success" role="alert">
                <h4 className="alert-heading">Pros</h4>
                <hr />
                {prosConsData.pros.some((pro) => pro.heading) ? (
                  <ul className="green-correct mb-2">
                    {[...new Set(prosConsData.pros
                      .filter((pro) => pro.heading)
                      .map((pro) => pro.heading)
                    )].map((uniquePro, index) => (
                      <li key={index}>{uniquePro}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No pros available</p>
                )}
              </div>
            </div>
          )}
          {prosConsData.cons.length > 0 && (
            <div className="col-sm-6">
              <div className="alert alert-secondary" role="alert">
                <h4 className="alert-heading">Cons</h4>
                <hr />
                {prosConsData.cons.some((con) => con.heading) ? (
                  <ul className="green-correct mb-2">
                    {[...new Set(prosConsData.cons
                      .filter((con) => con.heading)
                      .map((con) => con.heading)
                    )].map((uniqueCon, index) => (
                      <li key={index}>{uniqueCon}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No cons available</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CareerProsCons;